import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface UseHeroVisibilityReturn {
  isHeroVisible: boolean;
}

/**
 * Hook to detect when the hero section's logo is visible in the viewport
 * Used to conditionally show/hide the header logo on mobile
 *
 * @returns Object with isHeroVisible boolean - true when hero logo is visible
 */
export const useHeroVisibility = (): UseHeroVisibilityReturn => {
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const setupObserver = useCallback(() => {
    // Only observe on homepage
    if (!isHomePage) {
      setIsHeroVisible(false);
      return undefined;
    }

    const heroLogo = document.querySelector('[data-hero-logo]');
    if (!heroLogo) {
      // If hero logo not found, assume not visible (show header logo)
      setIsHeroVisible(false);
      return undefined;
    }

    // Use IntersectionObserver to detect when hero logo leaves viewport
    // rootMargin accounts for the fixed header height (64px)
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsHeroVisible(entry.isIntersecting);
      },
      {
        root: null, // Use viewport
        rootMargin: '-64px 0px 0px 0px', // Account for fixed header
        threshold: 0, // Trigger as soon as any part is visible/hidden
      }
    );

    observer.observe(heroLogo);
    return observer;
  }, [isHomePage]);

  useEffect(() => {
    // Small delay to ensure DOM is ready after route change
    const timeoutId = setTimeout(() => {
      const observer = setupObserver();
      return () => {
        if (observer) {
          observer.disconnect();
        }
      };
    }, 50);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [setupObserver]);

  // Reset to visible when navigating away from homepage
  useEffect(() => {
    if (!isHomePage) {
      setIsHeroVisible(false);
    }
  }, [isHomePage]);

  return { isHeroVisible };
};
